import React, {useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {toast} from 'react-toastify'
import {ModeleSavReferentiel, SavReferentielType} from '../../../../components/sav/Models'
import {
  createSavReferentiel,
  savErreur,
  updateSavReferentiel,
} from '../../../../components/sav/requests'
import {SAV_COMPORTEMENT_LIBELLES} from '../../../../components/sav/libelles'

type Props = {
  type: SavReferentielType
  titre: string
  valeur: ModeleSavReferentiel | null
  comportements: Array<string>
  onClose: () => void
  onSaved: () => void
}

// Création ou modification d'une valeur de référentiel SAV
const SavReferentielModal = ({type, titre, valeur, comportements, onClose, onSaved}: Props) => {
  const [loading, setLoading] = useState(false)
  const avecComportement = comportements.length > 0

  const schema = Yup.object().shape({
    libelle: Yup.string()
      .trim()
      .max(100, '100 caractères au plus')
      .required('Le libellé est requis'),
    comportement: avecComportement
      ? Yup.string().required('Le comportement est requis')
      : Yup.string(),
    ordre: Yup.number().integer().min(0),
  })

  const formik = useFormik<ModeleSavReferentiel>({
    initialValues: {
      libelle: valeur?.libelle ?? '',
      comportement: valeur?.comportement ?? '',
      retourParDefaut: valeur?.retourParDefaut ?? true,
      ordre: valeur?.ordre ?? 0,
      actif: valeur?.actif ?? true,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      setLoading(true)
      const donnees: ModeleSavReferentiel = {libelle: values.libelle, actif: values.actif}
      if (avecComportement) donnees.comportement = values.comportement
      if (type === 'motifs') donnees.retourParDefaut = values.retourParDefaut
      if (valeur?.id) donnees.ordre = Number(values.ordre)
      try {
        if (valeur?.id) {
          await updateSavReferentiel(type, valeur.id, donnees)
        } else {
          await createSavReferentiel(type, donnees)
        }
        toast.success('Enregistrement effectué')
        onSaved()
      } catch (e) {
        toast.error(savErreur(e))
      } finally {
        setLoading(false)
      }
    },
  })

  const erreur = (champ: keyof ModeleSavReferentiel) =>
    formik.touched[champ] && formik.errors[champ] ? (
      <div className='fv-plugins-message-container'>
        <div className='fv-help-block'>{formik.errors[champ] as string}</div>
      </div>
    ) : null

  return (
    <Modal show onHide={onClose} centered>
      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <Modal.Header closeButton>
          <Modal.Title>{valeur?.id ? `Modifier – ${titre}` : titre}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='mb-5'>
            <label className='form-label required fw-bold'>Libellé</label>
            <input
              className='form-control form-control-solid'
              {...formik.getFieldProps('libelle')}
            />
            {erreur('libelle')}
          </div>
          {avecComportement && (
            <div className='mb-5'>
              <label className='form-label required fw-bold'>Comportement dans KOENA</label>
              <select className='form-select' {...formik.getFieldProps('comportement')}>
                <option value=''>Choisir un comportement</option>
                {comportements.map((code) => (
                  <option key={code} value={code}>
                    {SAV_COMPORTEMENT_LIBELLES[code] ?? code}
                  </option>
                ))}
              </select>
              {erreur('comportement')}
            </div>
          )}
          {type === 'motifs' && (
            <div className='form-check form-switch mb-5'>
              <input
                className='form-check-input'
                type='checkbox'
                id='retourParDefaut'
                checked={!!formik.values.retourParDefaut}
                onChange={(e) => formik.setFieldValue('retourParDefaut', e.target.checked)}
              />
              <label className='form-check-label fw-bold' htmlFor='retourParDefaut'>
                Retour de la pièce requis par défaut
              </label>
            </div>
          )}
          {valeur?.id && (
            <div className='mb-5'>
              <label className='form-label fw-bold'>Ordre d'affichage</label>
              <input
                type='number'
                min={0}
                className='form-control form-control-solid'
                {...formik.getFieldProps('ordre')}
              />
              {erreur('ordre')}
            </div>
          )}
          <div className='form-check form-switch'>
            <input
              className='form-check-input'
              type='checkbox'
              id='actif'
              checked={!!formik.values.actif}
              onChange={(e) => formik.setFieldValue('actif', e.target.checked)}
            />
            <label className='form-check-label fw-bold' htmlFor='actif'>
              Actif (proposé dans les listes)
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={loading}>
            Annuler
          </button>
          <button type='submit' className='btn btn-success' disabled={loading}>
            {loading ? (
              <span className='indicator-progress' style={{display: 'block'}}>
                Enregistrement...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            ) : (
              'Enregistrer'
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default SavReferentielModal
